import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import img1 from "../../assets/images/recommend-slides-1.png";
import img2 from "../../assets/images/recommend-slides-2.png";
import img3 from "../../assets/images/recommend-slides-3.png";
import { getActiveHRVProperties } from '../../api/hrv';
import '../../css/recommendation.css';

const isMobileDevice = /Mobi|Android|iPhone|iPod/i.test(navigator.userAgent);

const isMobileDesktopMode = isMobileDevice && window.innerWidth >= 500;

const responsive = isMobileDesktopMode
  ? {
      desktop: {
        breakpoint: { max: 3000, min: 0 },
        items: 2,
        slidesToSlide: 1,
        partialVisibilityGutter: 20,
      },
    }
  : {

      desktop: {
        breakpoint: { max: 3000, min: 1024 },
        items: 3,
        slidesToSlide: 1,
        partialVisibilityGutter: 40,
      },
      tablet: {
        breakpoint: { max: 1024, min: 464 },
        items: 2,
        slidesToSlide: 1,
        partialVisibilityGutter: 30,
      },
      mobile: {
        breakpoint: { max: 464, min: 0 },
        items: 1,
        slidesToSlide: 1,
        partialVisibilityGutter: 20,
      },
    };


// Inner carousel (for hotel images)
const innerResponsive = {
  all: {
    breakpoint: { max: 3000, min: 0 },
    items: 1, // always 1 image at a time
  },
};

const HotelCard = ({ hotel, isFromAPI = true }) => {
  // Handle images for API data
  const getImages = () => {
    // For API data, use direct image URLs or fallback to static images
    return hotel.images && hotel.images.length > 0
      ? hotel.images
      : [img1, img2, img3];
  };

  const images = getImages();
  const hotelName = hotel.title; // API uses 'title'

  return (
    <div className="hotel-card">
      {/* Inner image slider */}
      <Carousel
        responsive={innerResponsive}
        infinite
        arrows
        showDots={false}
        swipeable
        draggable
        autoPlay={false}
        containerClass="hotel-images"
        itemClass="image-slide"
      >
        {images.map((img, idx) => (
          <img key={idx} src={img} alt={`${hotelName} ${idx}`} />
        ))}
      </Carousel>

      {/* Hotel info */}
      <a
        href={hotel.bookingUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        <div className="hotel-info">
          <h3 className="hotel-title">{hotelName}</h3>
          <p className="hotel-location">{hotel.location}</p>
          <div className="hotel-pricing">
            <div className="rates-text">Rates starts from</div>
            <div className="price-section">
              <span className="hotel-price">₱ {hotel.price}</span>
              <span className="per-night">/ night</span>
            </div>
          </div>
        </div>
      </a>
    </div>
  );
};

export default function RecommendationSlides() {
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;

  useEffect(() => {
    const fetchHRVData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getActiveHRVProperties();

        if (response.success && response.data && response.data.length > 0) {
          // Data is already sorted by newest first from the backend
          setHotels(response.data);
        } else {
          // No properties available
          setHotels([]);
        }
      } catch (err) {
        console.error('Failed to fetch HRV data:', err);
        setError(err.message);
        setHotels([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHRVData();
  }, []);

  // Pagination logic
  const totalPages = Math.ceil(hotels.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentHotels = hotels.slice(startIndex, endIndex);

  const goToNextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const goToPrevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const handleSeeAllProperties = () => {
    window.open('/properties', '_blank');
  };

  if (loading) {
    return (
      <section className="recommendation">
        <div className="container">
          <h2>Staycationary</h2>
          <div className="loading-state">
            <p>Loading properties...</p>
          </div>
        </div>
      </section>
    );
  }

  if (hotels.length === 0) {
    return (
      <section className="recommendation">
        <div className="container">
          <h2>Staycationary</h2>
          <div className="no-properties-state">
            {error ? (
              <>
                <p>Unable to load properties at the moment.</p>
                <p className="error-detail">{error}</p>
              </>
            ) : (
              <p>No hotels, resorts or vacation houses available at the moment.</p>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="recommendation">
      <div className="container">
        <div className="recommendation-header">
          <div className="header-left">
            <h2>Staycationary</h2>
          </div>
          {totalPages > 1 && (
            <div className="page-info">
              Page {currentPage + 1} of {totalPages} ({hotels.length} properties)
              <button onClick={handleSeeAllProperties} className="see-all-link" style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                See all properties <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          )}
        </div>

        <Carousel
          responsive={responsive}
          infinite={false}
          swipeable
          draggable
          arrows
          autoPlay={false}
          showDots={false}
          partialVisible
          containerClass="carousel-container"
        >
          {currentHotels.map((hotel, index) => (
            <HotelCard key={hotel._id || hotel.id || index} hotel={hotel} isFromAPI={true} />
          ))}
        </Carousel>

        {totalPages > 1 && (
          <div className="pagination-container">
            <button
              onClick={goToPrevPage}
              disabled={currentPage === 0}
              className={`pagination-btn prev-btn ${currentPage === 0 ? 'disabled' : ''}`}
            >
              Previous
            </button>

            <div className="pagination-numbers">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => goToPage(i)}
                  className={`pagination-btn page-btn ${i === currentPage ? 'active' : ''}`}
                >
                  {i + 1}
                </button>
              ))}
            </div>

            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages - 1}
              className={`pagination-btn next-btn ${currentPage === totalPages - 1 ? 'disabled' : ''}`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </section>
  );
}